import { fromPairs, toPairs } from 'lodash';
import { parse, ParsedQuery, stringify } from 'query-string';
import { useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router';
import { useLatest } from './use-latest';

export interface Transformer<T> {
  from: (parsed: ParsedQuery) => T;
  to: (value: T) => ParsedQuery;
}

export const DEFAULT_TRANSFORMER: Transformer<ParsedQuery> = {
  from: (parsed) => parsed,
  to: (value) => value,
};

const sanitize = (value: ParsedQuery): ParsedQuery =>
  fromPairs(toPairs(value).filter(([, x]) => x != null));

export const useHash = <T = ParsedQuery>({ from, to }: Transformer<T>) => {
  const { search, hash } = useLocation();
  const { push, replace: replaceFn } = useHistory();
  const parsed = useMemo(() => sanitize(parse(hash)), [hash]);
  const value = useMemo(() => from(parsed), [parsed, from]);
  const getNext = useCallback(
    (next: T) => `#${stringify(sanitize({ ...parsed, ...to(next) }))}`,
    [parsed, to],
  );
  const setValue = useCallback(
    (next: T, replace = false) => {
      const next_hash = getNext(next);

      if (hash === next_hash) {
        return;
      }

      (replace ? replaceFn : push)({
        hash: next_hash,
        search,
      });
    },
    [search, push, getNext, replaceFn, hash],
  );
  const setValueLatest = useLatest(setValue);
  const getNextLatest = useLatest(getNext);

  return [value, setValueLatest, getNextLatest] as [
    T,
    (value: T, replace?: boolean) => void,
    (value: T) => string,
  ];
};
